// Tema compartilhado entre os apps da fase 2.
// Aplica/lê classe `dark` no <html>, persiste em localStorage.
// Atalhos Ctrl+/ e Ctrl+\ alternam o tema (light/dark) globalmente.
// Sidebar tem botão dedicado (Ctrl+B continua alternando sidebar por compat).

export type Theme = "light" | "dark";

const STORAGE_KEY = "simulador-theme";

export function getStoredTheme(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch {
    return null;
  }
}

export function getActiveTheme(): Theme {
  if (typeof document === "undefined") return "dark";
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

export function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.toggle("dark", theme === "dark");
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // ignora
  }
  // dispatch evento pro UI reagir
  window.dispatchEvent(new CustomEvent("theme:changed", { detail: { theme } }));
}

export function toggleTheme(): Theme {
  const next: Theme = getActiveTheme() === "dark" ? "light" : "dark";
  applyTheme(next);
  return next;
}

export function initTheme(): Theme {
  const stored = getStoredTheme();
  const prefersDark =
    typeof matchMedia !== "undefined" &&
    matchMedia("(prefers-color-scheme: dark)").matches;
  const initial: Theme = stored ?? (prefersDark ? "dark" : "dark"); // default dark
  applyTheme(initial);
  return initial;
}

// Atalhos globais:
//   Ctrl+/ e Ctrl+\ → alternam o TEMA (light/dark)
//   Ctrl+B          → alterna a SIDEBAR do portal (compat com versão antiga)
// O App de cada portal passa seu próprio `toggleSidebar` (que mexe no signal
// local de sidebarCollapsed/sidebarMobileOpen, conforme breakpoint).
// Retorna função pra desligar (cleanup).
export function mountSidebarShortcuts(toggleSidebar: () => void): () => void {
  function handler(e: KeyboardEvent) {
    if (!(e.ctrlKey || e.metaKey)) return;
    if (e.altKey || e.shiftKey) return;
    const target = e.target as HTMLElement | null;
    const tag = target?.tagName;
    const editable = target?.isContentEditable;
    if (editable || tag === "INPUT" || tag === "TEXTAREA") return;
    if (e.key === "/" || e.key === "\\") {
      e.preventDefault();
      toggleTheme();
      return;
    }
    if (e.key === "b" || e.key === "B") {
      e.preventDefault();
      toggleSidebar();
      return;
    }
  }
  window.addEventListener("keydown", handler);
  return () => window.removeEventListener("keydown", handler);
}
